import SuperFetch from 'library/helpers/superFetch';
import { call, put, select, takeLatest, takeLeading } from 'redux-saga/effects';
import { apiFailureMessage } from 'library/helpers/apiFailureMessage';
import { Notification } from 'zComponents/atoms';
import { auditLogActions } from './slice';

// The read API caps pageSize at 1000 (routesV5/audit/schema.js), so export walks pages. Hitting
// EXPORT_MAX_ROWS is reported to the caller: a truncated export that looks complete is the failure.
const EXPORT_PAGE_SIZE = 1000;
const EXPORT_MAX_ROWS = 20000;

const compactQuery = (query = {}) =>
	Object.fromEntries(
		Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== '')
	);

// ⚠️ SuperFetch is base(method, slug, body, queryParam): filters MUST be the 3rd arg. A GET drops its body,
// so filters passed as the 2nd arg are silently ignored while the request still returns rows.
const fetchAuditPage = (query) => SuperFetch.get('/audit', {}, compactQuery(query));

// Errors go through the shared apiFailureMessage (prefers `info` where the 403 reason lives, hides raw 5xx,
// handles expired tokens); don't re-implement it locally or the copy misses its fixes.

export function* getAuditLogs({ payload = {} }) {
	try {
		const res = yield call(fetchAuditPage, payload);
		if (res.statusCode === 200) {
			yield put(
				auditLogActions.loadAuditLogsSuccess({
					data: res.data || [],
					summary: res.info?.summary,
					pagination: res.pagination || {}
				})
			);
		} else {
			throw Error(apiFailureMessage(res, 'Failed to load audit log.'));
		}
	} catch (error) {
		const message = error.message || String(error);
		yield put(auditLogActions.loadAuditLogsError(message));
		Notification('error', message);
	}
}

// Next page of the query on screen; the success carries its querySeq so a stale page can be dropped.
export function* loadMoreAuditLogs() {
	const { query, querySeq, pagination } = yield select((state) => state.AuditLogs);
	if (!query) return;
	try {
		// Keyset when the server hands a cursor (no shifting window); page numbers otherwise.
		const next = pagination?.nextBeforeAuditID;
		const res = yield call(
			fetchAuditPage,
			next != null
				? { ...query, page: undefined, beforeAuditID: next }
				: { ...query, page: (pagination?.page || 1) + 1 }
		);
		if (res.statusCode !== 200) throw Error(apiFailureMessage(res, 'Failed to load older audit events.'));
		yield put(
			auditLogActions.loadMoreAuditLogsSuccess({
				querySeq,
				data: res.data || [],
				pagination: res.pagination || {}
			})
		);
	} catch (error) {
		yield put(auditLogActions.loadMoreAuditLogsError());
		Notification('error', error.message || String(error));
	}
}

// Every row matching the filters, for export: follows the server cursor (page numbers if absent)
// until a short page, a null cursor, the total or the row cap.
export function* exportAuditLogs({ payload = {} }) {
	const { format = 'xlsx', ...query } = payload;
	try {
		const rows = [];
		const seen = new Set();
		let page = 1;
		// null, NOT 0: seeded at 0, the first full page satisfies `rows.length >= total` and the export
		// stops after one page with truncated: false, a capped file presented as complete.
		let total = null;
		let truncated = false;

		// Three bounds: the server's reported total, an empty batch, and the row cap — so an
		// absent or disagreeing total cannot produce an unbounded loop OR a short file.
		let cursor = null;
		for (;;) {
			const res = yield call(fetchAuditPage, {
				...query,
				...(cursor != null ? { beforeAuditID: cursor } : { page }),
				pageSize: EXPORT_PAGE_SIZE
			});
			if (res.statusCode !== 200) throw Error(apiFailureMessage(res, 'Failed to export audit log.'));

			const batch = res.data || [];
			const reported = Number(res.pagination?.total);
			if (Number.isFinite(reported)) total = reported;
			// Safety net for OFFSET paging, where an event logged mid-walk repeats a row: keep each auditID once.
			batch.forEach((row) => {
				if (seen.has(row.auditID)) return;
				seen.add(row.auditID);
				rows.push(row);
			});

			// A short page, or a null cursor, means the server has no more, whatever it claimed the total was.
			const nextCursor = res.pagination?.nextBeforeAuditID;
			if (!batch.length || batch.length < EXPORT_PAGE_SIZE || nextCursor === null) break;
			if (total !== null && rows.length >= total) break;
			if (rows.length >= EXPORT_MAX_ROWS) {
				truncated = true;
				break;
			}
			if (nextCursor != null) cursor = nextCursor;
			else page += 1;
		}

		// A zero-match export would otherwise write a headerless, entirely empty file — which
		// looks identical to a failed export. Say which it was instead.
		if (!rows.length) {
			yield put(auditLogActions.clearAuditLogExport());
			Notification('info', 'No audit events match these filters, so there is nothing to export.');
			return;
		}

		yield put(auditLogActions.exportAuditLogsSuccess({ rows, format, total, truncated }));
		if (truncated) {
			Notification(
				'warning',
				`Export capped at ${rows.length.toLocaleString()}${
					total === null ? '' : ` of ${total.toLocaleString()}`
				} matching events. Narrow the filters to export the rest.`
			);
		}
	} catch (error) {
		const message = error.message || String(error);
		yield put(auditLogActions.exportAuditLogsError(message));
		Notification('error', message);
	}
}

export default function* auditLogsSaga() {
	yield takeLatest(auditLogActions.loadAuditLogs, getAuditLogs);
	yield takeLeading(auditLogActions.loadMoreAuditLogs, loadMoreAuditLogs);
	yield takeLatest(auditLogActions.exportAuditLogs, exportAuditLogs);
}
